import { useEffect, useState } from "react";

import { generatePath } from "react-router-dom";

import { postTrackingData } from "@/api/endpoints/track";
import ArrowTopRightOnSquareIcon from "@/assets/icons/arrow-top-right-on-square.svg";
import ChatBubbleIcon from "@/assets/icons/chat-bubble.svg";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { FEATURE } from "@/features";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { stringifyUrl } from "@/utils";
import copyToClipboard from "@/utils/copyToClipboard";
import AlertShareExportCard from "@components/AlertShareExportCard";
import Button from "@components/Button";
import { Feature } from "@components/Feature";
import { Checkbox } from "@components/Field";
import TextButton from "@components/TextButton";

import styles from "../TagShareModal.module.css";

const Share = ({ className }: { className?: string }) => {
  const [copied, setCopied] = useState(false);
  const { tag } = useMentionCards();
  const { data: user } = useUser();
  const { language = "sv" } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { uuid: organization_uuid = "" } = orgData?.ok ? orgData.data : {};
  const { danger } = useMessages();
  const [shouldShowKpis, setshouldShowKpis] = useState(true);

  const shareUrl = stringifyUrl({
    url: `${window.location.origin}${generatePath(ROUTES.TAG_PUBLIC, {
      language: language,
      id: tag?.public_token,
    })}`,
    query: shouldShowKpis
      ? { kpis: shouldShowKpis, sentiment: shouldShowKpis }
      : {},
  });

  useEffect(() => {
    let mounted = true;

    if (copied) {
      setTimeout(() => {
        mounted && setCopied(false);
      }, 3000);
    }

    return () => {
      mounted = false;
    };
  }, [copied]);

  const handleClipboardCopy = async () => {
    try {
      await copyToClipboard(shareUrl);
      postTrackingData(EVENT.SHARE_TAG_COPIED, tag);
      setCopied(true);
    } catch {
      danger({
        title: "",
        message: L.MENTIONS_SHARE_COPY_TO_CLIPBOARD_FAILED,
      });
    }
  };

  return (
    <div className={className}>
      {shareUrl ? (
        <AlertShareExportCard.Root>
          <AlertShareExportCard.Left>
            <AlertShareExportCard.Icon icon={ChatBubbleIcon} />
            <AlertShareExportCard.LeftText>
              <AlertShareExportCard.Title>
                {L.SHARE_ALERT_MODAL_MENTIONS}
              </AlertShareExportCard.Title>
              <Checkbox
                label={L.SHARE_WITH_ALL_KPIS}
                checked={shouldShowKpis}
                onChange={(e) => setshouldShowKpis(e.target.checked)}
              />
            </AlertShareExportCard.LeftText>
          </AlertShareExportCard.Left>

          <AlertShareExportCard.Right>
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="share-mentions-link-preview"
              onClick={() => {
                postTrackingData(EVENT.SHARE_TAG_CLICKED, {
                  shareUrl,
                });
              }}
            >
              <ArrowTopRightOnSquareIcon width="1.25rem" height="1.25rem" />
            </a>

            <Button onClick={handleClipboardCopy} className="ml-3" size="md">
              {copied ? L.SHARE_MODAL_COPY_URL_SUCCESS : L.SHARE_MODAL_COPY_URL}
            </Button>
          </AlertShareExportCard.Right>
        </AlertShareExportCard.Root>
      ) : null}
      <div className={styles.links}>
        <Feature required={FEATURE.HAS_RSS_FEATURE}>
          <TextButton
            color="primary"
            href={generatePath(ROUTES.TAG_EXPORT_RSS, {
              organization_uuid: organization_uuid,
              slug: tag?.slug,
              language,
            })}
            target="_blank"
            icon={ArrowTopRightOnSquareIcon}
          >
            {L.SHARE_TAG_MODAL_RSS}
          </TextButton>
        </Feature>
      </div>
    </div>
  );
};
export default Share;
